import React, { useState, useRef, useCallback } from 'react';
import {
  ArrowUpTrayIcon,
  DocumentTextIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  EyeIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../../services/api';
import PageLayout from '../../components/PageLayout';
import Tabs from '../../components/ui/Tabs';
import { toast } from 'react-hot-toast';
import {
  formatImportExecutionToast,
  getImportExecutionCounts,
  isRecoverableImportExecutionFailure,
} from '../../utils/importExecution';

// --- Interfaces ---

type ContactReconciliationAction =
  | 'use_for_invoice_only'
  | 'add_as_account_billing_contact'
  | 'set_as_primary_billing_contact'
  | 'update_matched_customer_contact';

interface BillingContactSnapshot {
  name: string;
  email: string;
  phone: string;
  company_name: string;
}

interface BatchSummary {
  id: string;
  source_filename: string;
  source_type: 'csv' | 'pdf' | 'email';
  status:
    | 'pending_review'
    | 'approved'
    | 'processing'
    | 'completed'
    | 'completed_with_issues'
    | 'failed'
    | 'cancelled';
  total_items: number;
  valid_items: number;
  invalid_items: number;
  skipped_items: number;
  invoices_created: number;
  uploaded_by: string;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

interface BatchItem {
  row_number: number;
  reservation_ref: string;
  pickup_date: string;
  pickup_time: string;
  scsw_customer_id: string;
  customer_email: string;
  customer_phone: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_company_name: string;
  billing_contact_name: string;
  billing_contact_email: string;
  billing_contact_phone: string;
  billing_company_name: string;
  passenger_name: string;
  vehicle_type: string;
  driver: string;
  vehicle: string;
  pickup_location: string;
  dropoff_location: string;
  total: number;
  rate: number;
  stop_charge: number;
  wait_charge: number;
  tolls: number;
  parking: number;
  gratuity: number;
  service_charge: number;
  discount: number;
  taxes: number;
  other_charges: number;
  matched_customer_id: string | null;
  matched_customer_name: string | null;
  validation_status: 'valid' | 'warning' | 'error';
  validation_errors: string[];
  skipped: boolean;
  original_invoice_number: string | null;
  contact_reconciliation_action?: ContactReconciliationAction | null;
  contact_reconciliation_result?: {
    action: ContactReconciliationAction;
    status: 'not_required' | 'applied' | 'skipped' | 'failed';
    account_id?: string | null;
    customer_id?: string | null;
    stripe_customer_id?: string | null;
    message?: string;
  } | null;
  imported_billing_contact?: BillingContactSnapshot | null;
  saved_contact?: BillingContactSnapshot | null;
  billing_contact_mismatch?: boolean;
  available_contact_actions?: ContactReconciliationAction[];
  imported_account_name?: string | null;
  matched_account_id?: string | null;
  matched_account_name?: string | null;
  account_name_mismatch?: boolean;
}

interface BatchDetail extends BatchSummary {
  items: BatchItem[];
  processing_result: any;
}

interface PreviewCustomer {
  customer_id: string | null;
  customer_name: string;
  scsw_customer_id: string;
  customer_email: string;
  line_items: BatchItem[];
  projected_total: number;
}

interface StripeCustomerEmailMatch {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  scsw_customer_id: string | null;
}

interface DeferredCustomerResolution {
  scsw_customer_id: string;
  customer_name: string;
  customer_email: string;
  item_count: number;
  row_numbers: number[];
  resolution_type: 'link_existing' | 'create_on_finalize';
  preferred_stripe_customer: StripeCustomerEmailMatch | null;
  matching_stripe_customers: StripeCustomerEmailMatch[];
}

interface BatchPreview {
  customers: PreviewCustomer[];
  unmatched_items: BatchItem[];
  skipped_items: BatchItem[];
  duplicate_items: BatchItem[];
  deferred_customer_resolution: DeferredCustomerResolution[];
  summary: {
    total_customers: number;
    total_invoices_amount: number;
    total_line_items: number;
    accepted_items: number;
    rejected_items: number;
    duplicate_items: number;
    deferred_items: number;
  };
}

interface Adjustment {
  row_number: number;
  matched_customer_id?: string;
  invoice_group?: string;
  exclude?: boolean;
  contact_reconciliation_action?: ContactReconciliationAction;
}

// --- Constants ---

const statusColors: Record<string, string> = {
  pending_review: 'bg-warning-container text-warning-on-container',
  approved: 'bg-info-container text-info-on-container',
  processing: 'bg-primary/15 text-primary',
  completed: 'bg-success-container text-success-on-container',
  completed_with_issues: 'bg-warning-container text-warning-on-container',
  failed: 'bg-error-container text-error-on-container',
  cancelled: 'bg-surface-container-high text-on-surface',
};

const statusLabels: Record<string, string> = {
  pending_review: 'Pending Review',
  approved: 'Approved',
  processing: 'Processing',
  completed: 'Completed',
  completed_with_issues: 'Completed With Issues',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const contactActionLabels: Record<ContactReconciliationAction, string> = {
  use_for_invoice_only: 'Use for invoice only',
  add_as_account_billing_contact: 'Add billing contact',
  set_as_primary_billing_contact: 'Set account primary billing',
  update_matched_customer_contact: 'Update matched customer',
};

const contactActionDescriptions: Record<ContactReconciliationAction, string> = {
  use_for_invoice_only: 'Stores this contact on the imported invoice only.',
  add_as_account_billing_contact: 'Adds this contact to the matched account without changing the matched customer.',
  set_as_primary_billing_contact: 'Sets this as the account primary billing contact; matched customer stays unchanged.',
  update_matched_customer_contact: 'Updates the matched SCSW customer and syncs that customer to Stripe when available.',
};

function getContactResultMessage(result: BatchItem['contact_reconciliation_result']): string {
  if (!result) return '';
  if (result.message) {
    if (
      result.action === 'set_as_primary_billing_contact'
      && result.status === 'applied'
      && !result.message.toLowerCase().includes('matched customer')
    ) {
      return `${result.message} Matched customer unchanged.`;
    }
    return result.message;
  }
  return contactActionDescriptions[result.action] || contactActionLabels[result.action];
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatContact(contact?: BillingContactSnapshot | null): string {
  if (!contact) return 'None';
  return [contact.name, contact.email, contact.phone, contact.company_name]
    .filter(Boolean)
    .join(' · ') || 'None';
}

function invalidateImportRelatedQueries(queryClient: ReturnType<typeof useQueryClient>, batchId?: string | null) {
  queryClient.invalidateQueries(['import-batches']);
  if (batchId) {
    queryClient.invalidateQueries(['import-batch', batchId]);
    queryClient.invalidateQueries(['import-batch-preview', batchId]);
  }
  queryClient.invalidateQueries('admin-invoices');
  queryClient.invalidateQueries('admin-customers');
  queryClient.invalidateQueries('admin-accounts');
  queryClient.invalidateQueries('customer-list');
  queryClient.invalidateQueries('account-list');
}

async function recoverImportExecution(batchId: string) {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    if (attempt > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, 3000));
    }
    const response = await adminAPI.getImportBatch(batchId);
    const batch = response.data as BatchSummary;
    if (
      batch.status === 'completed' ||
      batch.status === 'completed_with_issues'
    ) {
      return response;
    }
    if (batch.status === 'failed' || batch.status === 'cancelled') {
      throw new Error(`Import batch ${batch.status}`);
    }
  }

  throw new Error('Import is still processing. Its status will continue updating in the batch list.');
}

// ============================================================
// Main Component
// ============================================================

export default function AdminInvoiceImport() {
  const queryClient = useQueryClient();
  const csvInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const aiPdfInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<'upload' | 'batches'>('upload');
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [showReview, setShowReview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState<'csv' | 'pdf' | null>(null);
  const [uploadElapsed, setUploadElapsed] = useState(0);
  const [paymentTerms, setPaymentTerms] = useState<string>('due_on_receipt');
  const [taxRate, setTaxRate] = useState<string>('');

  // Queries
  const batchesQuery = useQuery(
    ['import-batches'],
    () => adminAPI.getImportBatches({ limit: 50 }),
    { refetchInterval: 10000 }
  );

  const batchDetailQuery = useQuery(
    ['import-batch', selectedBatchId],
    () => adminAPI.getImportBatch(selectedBatchId!),
    { enabled: !!selectedBatchId }
  );

  const batchPreviewQuery = useQuery(
    ['import-batch-preview', selectedBatchId],
    () => adminAPI.getImportBatchPreview(selectedBatchId!),
    { enabled: !!selectedBatchId }
  );

  // Mutations
  const executeMutation = useMutation(
    async (batchId: string) => {
      try {
        return await adminAPI.executeImportBatch(batchId);
      } catch (error) {
        if (!isRecoverableImportExecutionFailure(error)) {
          throw error;
        }
        return recoverImportExecution(batchId);
      }
    },
    {
      onSuccess: (res) => {
        const data = res.data;
        const counts = getImportExecutionCounts(data);
        if (counts.failedGroups > 0) {
          toast.error(formatImportExecutionToast(data));
        } else {
          toast.success(formatImportExecutionToast(data));
        }
        invalidateImportRelatedQueries(queryClient, selectedBatchId);
        setShowReview(false);
      },
      onError: (err: any) => { toast.error(err?.response?.data?.message || 'Failed to execute batch'); },
    }
  );

  const cancelMutation = useMutation(
    (batchId: string) => adminAPI.cancelImportBatch(batchId),
    {
      onSuccess: () => {
        toast.success('Batch cancelled');
        queryClient.invalidateQueries(['import-batches']);
        setSelectedBatchId(null);
        setShowReview(false);
      },
      onError: (err: any) => { toast.error(err?.response?.data?.message || 'Failed to cancel batch'); },
    }
  );

  const deleteMutation = useMutation(
    (batchId: string) => adminAPI.deleteImportBatch(batchId),
    {
      onSuccess: () => {
        toast.success('Batch deleted');
        queryClient.invalidateQueries(['import-batches']);
        setSelectedBatchId(null);
        setShowReview(false);
      },
      onError: (err: any) => { toast.error(err?.response?.data?.message || 'Failed to delete batch'); },
    }
  );

  const handleFileUpload = useCallback(async (file: File, type: 'csv' | 'pdf', method?: 'ai') => {
    setUploading(true);
    setUploadType(type);
    setUploadElapsed(0);
    const timer = setInterval(() => setUploadElapsed(prev => prev + 1), 1000);
    try {
      const options: { payment_terms?: string; tax_rate?: number } = {};
      if (paymentTerms) options.payment_terms = paymentTerms;
      if (taxRate) options.tax_rate = parseFloat(taxRate);

      const res = type === 'csv'
        ? await adminAPI.uploadInvoiceCsv(file, options)
        : await adminAPI.uploadInvoicePdf(file, { ...options, method: method || 'programmatic' });

      const data = res.data;
      toast.success(
        `Imported ${data.total_items} items (${data.valid_items} valid, ${data.invalid_items} invalid, ${data.skipped_items} skipped)`
      );
      queryClient.invalidateQueries(['import-batches']);
      setSelectedBatchId(data.batch_id);
      setShowReview(true);
      setActiveTab('batches');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || `Failed to upload ${type.toUpperCase()} file`);
    } finally {
      clearInterval(timer);
      setUploading(false);
      setUploadType(null);
      setUploadElapsed(0);
      if (csvInputRef.current) csvInputRef.current.value = '';
      if (pdfInputRef.current) pdfInputRef.current.value = '';
      if (aiPdfInputRef.current) aiPdfInputRef.current.value = '';
    }
  }, [paymentTerms, taxRate, queryClient]);

  const handleDownloadTemplate = async () => {
    try {
      const res = await adminAPI.downloadInvoiceImportTemplate();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'invoice-import-template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const batches: BatchSummary[] = batchesQuery.data?.data?.batches || [];
  const batchDetail: BatchDetail | null = batchDetailQuery.data?.data || null;

  return (
    <PageLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-on-surface">Invoice Import</h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          Upload CSV or PDF files, review customer/contact matches, and create invoices in batches.
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 overflow-x-auto">
        <Tabs
          tabs={[
            { key: 'upload', label: 'Upload', icon: <ArrowUpTrayIcon /> },
            { key: 'batches', label: 'Batches', icon: <DocumentTextIcon />, count: batches.length },
          ]}
          activeTab={activeTab}
          onChange={(tab) => {
            setActiveTab(tab as typeof activeTab);
            setSelectedBatchId(null);
            setShowReview(false);
          }}
          className="min-w-max"
        />
      </div>

      {/* Upload Progress Banner */}
      {uploading && (
        <div className="mb-6 rounded-card border border-info-container/70 bg-info-container/25 p-4">
          <div className="flex items-center space-x-3">
            <ArrowPathIcon className="w-5 h-5 text-info-on-container animate-spin flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-on-surface">
                {uploadType === 'pdf'
                  ? 'Processing PDF with AI extraction...'
                  : 'Parsing CSV file...'}
              </div>
              <div className="text-xs text-on-surface-variant mt-1">
                {uploadType === 'pdf'
                  ? `This may take 2-5 minutes for large PDFs. Elapsed: ${Math.floor(uploadElapsed / 60)}m ${uploadElapsed % 60}s`
                  : `Elapsed: ${Math.floor(uploadElapsed / 60)}m ${uploadElapsed % 60}s`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <div className="space-y-6">
          <div className="card p-6">
            <h3 className="text-lg font-medium text-on-surface mb-4">Import Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Payment Terms</label>
                <select
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  className="input-field text-sm"
                >
                  <option value="due_on_receipt">Due on Receipt</option>
                  <option value="net_15">Net 15</option>
                  <option value="net_30">Net 30</option>
                  <option value="net_60">Net 60</option>
                  <option value="net_90">Net 90</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Tax Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  className="input-field text-sm"
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card p-6">
              <div className="text-center">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-on-surface-variant" />
                <h3 className="mt-2 text-lg font-medium text-on-surface">CSV Import</h3>
                <p className="mt-1 text-sm text-on-surface-variant">Upload a CSV file with trip data to create invoices.</p>
                <div className="mt-4 space-y-3">
                  <input ref={csvInputRef} type="file" accept=".csv" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f, 'csv'); }} />
                  <button onClick={() => csvInputRef.current?.click()} disabled={uploading}
                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                    {uploading ? <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpTrayIcon className="w-4 h-4 mr-2" />}
                    Upload CSV
                  </button>
                  <button onClick={handleDownloadTemplate} className="mx-auto inline-flex items-center text-sm font-medium text-primary hover:underline">
                    <DocumentArrowDownIcon className="w-4 h-4 inline mr-1" /> Download Template
                  </button>
                </div>
              </div>
            </div>

            {/* <div className="bg-surface-container-lowest shadow-ambient rounded-card p-6">
              <div className="text-center">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-red-400" />
                <h3 className="mt-2 text-lg font-medium text-on-surface">PDF Import</h3>
                <p className="mt-1 text-sm text-gray-500">Upload a SantaCruz Trip Activity PDF report.</p>
                <div className="mt-4 space-y-3">
                  <input ref={pdfInputRef} type="file" accept=".pdf" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f, 'pdf'); }} />
                  <button onClick={() => pdfInputRef.current?.click()} disabled={uploading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                    {uploading && uploadType === 'pdf' ? <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpTrayIcon className="w-4 h-4 mr-2" />}
                    Upload PDF
                  </button>
                  <div className="border-t border-ghost pt-3 mt-3">
                    <input ref={aiPdfInputRef} type="file" accept=".pdf" className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f, 'pdf', 'ai'); }} />
                    <button onClick={() => aiPdfInputRef.current?.click()} disabled={uploading}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed">
                      Upload with AI (slower, costs per use)
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">Default uses fast text extraction (free). AI option available for difficult PDFs.</p>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      )}

      {/* Batches List */}
      {activeTab === 'batches' && !selectedBatchId && (
        <div className="admin-table-card">
          <table className="min-w-full divide-y divide-outline">
            <thead className="bg-surface-container-low">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">File</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Invoices</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-surface-container-lowest divide-y divide-outline">
              {batches.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-on-surface-variant">No import batches yet. Upload a file to get started.</td></tr>
              ) : batches.map((batch) => (
                <tr key={batch.id} data-batch-id={batch.id} className="hover:bg-surface-container-low">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-on-surface">{batch.source_filename}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant uppercase">{batch.source_type}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[batch.status]}`}>{statusLabels[batch.status]}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                    <span className="text-success-on-container">{batch.valid_items}</span>
                    {batch.invalid_items > 0 && <span className="text-error-on-container ml-1">/ {batch.invalid_items} err</span>}
                    {batch.skipped_items > 0 && <span className="text-on-surface-variant ml-1">/ {batch.skipped_items} skip</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">{batch.invoices_created || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">{formatDate(batch.created_at)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button onClick={() => { setSelectedBatchId(batch.id); setShowReview(batch.status === 'pending_review'); }}
                      className="font-medium text-primary hover:underline mr-3">
                      <EyeIcon className="w-4 h-4 inline mr-1" />
                      {batch.status === 'pending_review' ? 'Review' : 'View'}
                    </button>
                    {batch.status !== 'processing' && (
                      <button onClick={() => { if (window.confirm('Delete this batch?')) deleteMutation.mutate(batch.id); }}
                        className="text-error hover:underline">
                        <TrashIcon className="w-4 h-4 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Review / Detail View */}
      {activeTab === 'batches' && selectedBatchId && batchDetail && (
        <BatchReviewView
          batch={batchDetail}
          batchPreview={batchPreviewQuery.data?.data}
          isReviewMode={showReview && batchDetail.status === 'pending_review'}
          onBack={() => { setSelectedBatchId(null); setShowReview(false); }}
          onExecute={() => executeMutation.mutate(selectedBatchId)}
          onCancel={() => cancelMutation.mutate(selectedBatchId)}
          onDelete={() => { if (window.confirm('Delete this batch?')) deleteMutation.mutate(selectedBatchId); }}
          onRetryWithAi={async () => {
            // Re-upload the PDF with AI method - user needs to upload again
            toast.error('To retry with AI, upload the PDF again using the AI option.');
            setSelectedBatchId(null);
            setShowReview(false);
            setActiveTab('upload');
          }}
          isExecuting={executeMutation.isLoading}
          isCancelling={cancelMutation.isLoading}
          queryClient={queryClient}
        />
      )}

      {/* Loading */}
      {activeTab === 'batches' && selectedBatchId && (batchDetailQuery.isLoading || batchPreviewQuery.isLoading) && (
        <div className="flex justify-center py-12">
          <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      )}
    </PageLayout>
  );
}

// ============================================================
// Review View — interactive batch review
// ============================================================

function BatchReviewView({
  batch,
  batchPreview,
  isReviewMode,
  onBack,
  onExecute,
  onCancel,
  onDelete,
  onRetryWithAi,
  isExecuting,
  isCancelling,
  queryClient,
}: {
  batch: BatchDetail;
  batchPreview?: BatchPreview;
  isReviewMode: boolean;
  onBack: () => void;
  onExecute: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onRetryWithAi: () => void;
  isExecuting: boolean;
  isCancelling: boolean;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [filter, setFilter] = useState<'all' | 'valid' | 'unmatched' | 'skipped'>('all');
  const [sortBy, setSortBy] = useState<'row' | 'customer' | 'passenger' | 'date' | 'total' | 'status'>('row');
  const [adjustments, setAdjustments] = useState<Map<number, Adjustment>>(new Map());
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isApproving, setIsApproving] = useState(false);
  const [retryingInvoiceId, setRetryingInvoiceId] = useState<string | null>(null);

  const previewItemsByRow = new Map<number, BatchItem>();
  batchPreview?.customers?.forEach((customer) => {
    customer.line_items.forEach((item) => previewItemsByRow.set(item.row_number, item));
  });
  batchPreview?.unmatched_items?.forEach((item) =>
    previewItemsByRow.set(item.row_number, item)
  );
  batchPreview?.skipped_items?.forEach((item) =>
    previewItemsByRow.set(item.row_number, item)
  );
  batchPreview?.duplicate_items?.forEach((item) =>
    previewItemsByRow.set(item.row_number, item)
  );
  const items = (batch.items || []).map((item) => ({
    ...item,
    ...(previewItemsByRow.get(item.row_number) || {}),
  }));
  const deferredCustomerResolution = batchPreview?.deferred_customer_resolution || [];
  const deferredResolutionByRow = new Map<number, DeferredCustomerResolution>();
  for (const resolution of deferredCustomerResolution) {
    for (const rowNumber of resolution.row_numbers) {
      deferredResolutionByRow.set(rowNumber, resolution);
    }
  }

  // Apply local adjustments to compute effective state
  const getEffectiveItem = (item: BatchItem) => {
    const adj = adjustments.get(item.row_number);
    if (!adj) return item;
    return {
      ...item,
      matched_customer_id: adj.matched_customer_id ?? item.matched_customer_id,
      skipped: adj.exclude ?? item.skipped,
      contact_reconciliation_action: adj.contact_reconciliation_action ?? item.contact_reconciliation_action,
    };
  };

  const effectiveItems = items.map(getEffectiveItem);

  const filteredItems = effectiveItems.filter(item => {
    if (filter === 'valid') return item.validation_status === 'valid' && !item.skipped;
    if (filter === 'unmatched') return !item.matched_customer_id && item.validation_status === 'valid' && !item.skipped;
    if (filter === 'skipped') return item.skipped;
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'customer': return (a.scsw_customer_id || '').localeCompare(b.scsw_customer_id || '');
      case 'passenger': return (a.passenger_name || '').localeCompare(b.passenger_name || '');
      case 'date': return (a.pickup_date + a.pickup_time).localeCompare(b.pickup_date + b.pickup_time);
      case 'total': return b.total - a.total;
      case 'status': {
        const rank = (item: typeof a) => item.skipped ? 2 : item.matched_customer_id ? 0 : 1;
        return rank(a) - rank(b);
      }
      default: return a.row_number - b.row_number;
    }
  });

  const validCount = effectiveItems.filter(
    (i) => i.validation_status === 'valid' && !i.skipped
  ).length;
  const unmatchedCount = effectiveItems.filter(
    (i) =>
      !i.matched_customer_id && i.validation_status === 'valid' && !i.skipped
  ).length;
  const skippedCount = effectiveItems.filter((i) => i.skipped).length;
  const acceptedCount = batchPreview?.summary?.accepted_items ?? validCount;
  const rejectedCount =
    batchPreview?.summary?.rejected_items ?? batch.invalid_items;
  const duplicateCount = batchPreview?.summary?.duplicate_items ?? 0;
  const adjustmentCount = adjustments.size;
  const processingCounts = getImportExecutionCounts(batch);

  const handleExcludeItem = (rowNumber: number) => {
    setAdjustments(prev => {
      const next = new Map(prev);
      const existing = next.get(rowNumber);
      if (existing?.exclude) {
        // Toggle off
        if (existing.matched_customer_id) {
          next.set(rowNumber, { row_number: rowNumber, matched_customer_id: existing.matched_customer_id });
        } else {
          next.delete(rowNumber);
        }
      } else {
        next.set(rowNumber, { row_number: rowNumber, ...existing, exclude: true });
      }
      return next;
    });
  };

  const handleContactActionChange = (rowNumber: number, action: ContactReconciliationAction) => {
    setAdjustments(prev => {
      const next = new Map(prev);
      const existing = next.get(rowNumber);
      next.set(rowNumber, { row_number: rowNumber, ...existing, contact_reconciliation_action: action });
      return next;
    });
  };

  const toggleRowSelection = (rowNumber: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  };

  const handleApproveWithAdjustments = async () => {
    setIsApproving(true);
    try {
      const adjArray = Array.from(adjustments.values());
      await adminAPI.approveImportBatch(batch.id, adjArray);
      toast.success('Batch approved with adjustments');
      invalidateImportRelatedQueries(queryClient, batch.id);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to approve batch');
    } finally {
      setIsApproving(false);
    }
  };

  const handleRetryInvoiceSync = async (invoiceId: string) => {
    setRetryingInvoiceId(invoiceId);
    try {
      await adminAPI.retryInvoiceSync(invoiceId);
      toast.success('Invoice synchronized with Stripe');
      invalidateImportRelatedQueries(queryClient, batch.id);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          'Stripe synchronization is still pending recovery'
      );
    } finally {
      setRetryingInvoiceId(null);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="text-on-surface-variant hover:text-on-surface">&larr; Back to Batches</button>
          <h2 className="text-xl font-semibold text-on-surface">{batch.source_filename}</h2>
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[batch.status]}`}>
            {statusLabels[batch.status]}
          </span>
        </div>
        <div className="flex items-center space-x-3">
          {batch.status === 'pending_review' && (
            <>
              <button
                onClick={handleApproveWithAdjustments}
                disabled={isApproving}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                {isApproving ? 'Approving...' : adjustmentCount > 0 ? `Approve (${adjustmentCount} changes)` : 'Approve'}
              </button>
              {batch.source_type === 'pdf' && (
                <button onClick={onRetryWithAi}
                  className="inline-flex items-center px-3 py-2 border border-purple-300 rounded-md text-sm font-medium text-purple-700 bg-surface-container-lowest hover:bg-purple-50">
                  <ArrowPathIcon className="w-4 h-4 mr-1" /> Retry with AI
                </button>
              )}
              <button onClick={() => onCancel()} disabled={isCancelling}
                  className="btn-secondary inline-flex items-center gap-2 text-error disabled:opacity-50">
                <XCircleIcon className="w-4 h-4 mr-1" /> Cancel
              </button>
            </>
          )}
          {(batch.status === 'approved' ||
            batch.status === 'completed_with_issues' ||
            batch.status === 'failed') && (
            <button
              onClick={onExecute}
              disabled={isExecuting}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {isExecuting ? (
                <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircleIcon className="w-4 h-4 mr-2" />
              )}
              {isExecuting
                ? 'Creating Invoices...'
                : batch.status === 'approved'
                ? 'Execute — Create Invoices'
                : 'Retry Failed Work'}
            </button>
          )}
          {batch.status !== 'processing' && (
            <button onClick={onDelete}
              className="btn-secondary inline-flex items-center gap-2 text-error">
              <TrashIcon className="w-4 h-4 mr-1" /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <StatCard label="Total Items" value={items.length} />
        <StatCard label="Accepted" value={acceptedCount} color="green" />
        <StatCard label="Deferred" value={unmatchedCount} color="yellow" />
        <StatCard label="Rejected" value={rejectedCount} color="red" />
        <StatCard label="Duplicates" value={duplicateCount} color="gray" />
        {skippedCount > 0 && (
          <StatCard label="Skipped" value={skippedCount} color="gray" />
        )}
        <StatCard
          label="Invoices Created"
          value={batch.invoices_created}
          color="blue"
        />
      </div>

      {/* Processing Result */}
      {batch.processing_result && (
        <div
          className={`rounded-card border p-4 mb-6 ${
            processingCounts.overallState !== 'complete'
              ? 'border-warning-container/70 bg-warning-container/25'
              : 'border-success-container/70 bg-success-container/25'
          }`}
        >
          <h3 className="text-sm font-medium text-on-surface">
            {processingCounts.overallState !== 'complete'
              ? 'Processing Completed With Issues'
              : 'Processing Complete'}
          </h3>
          <p className="mt-1 text-sm text-on-surface-variant">
            Created {processingCounts.invoicesCreated} invoice(s)
            {processingCounts.duplicatesSkipped > 0 &&
              ` · ${processingCounts.duplicatesSkipped} matched existing invoice(s)`}
            {processingCounts.rowsSkipped > 0 &&
              ` · ${processingCounts.rowsSkipped} excluded or invalid row(s)`}
            {processingCounts.failedGroups > 0 &&
              ` · ${processingCounts.failedGroups} failed`}
            {processingCounts.unexplainedLocalOnly > 0 &&
              ` · ${processingCounts.unexplainedLocalOnly} unexplained provider state(s)`}
          </p>
          <p className="mt-1 text-xs text-on-surface-variant">
            Imported invoices are drafts. Stripe synchronization is required
            only after an invoice is finalized.
          </p>
          {Array.isArray(batch.processing_result.row_outcomes) &&
            batch.processing_result.row_outcomes.length > 0 && (
              <div className="mt-3 rounded-card bg-surface-container-lowest p-3 text-xs text-on-surface-variant">
                <p className="font-medium text-on-surface">Row outcomes</p>
                <ul className="mt-2 space-y-1">
                  {batch.processing_result.row_outcomes.map((entry: any) => (
                    <li
                      key={entry.row_number}
                      className="flex flex-wrap items-center justify-between gap-2"
                    >
                      <span>
                        Row {entry.row_number}:{' '}
                        {String(entry.status || 'unknown').replaceAll('_', ' ')}
                        {entry.invoice_number
                          ? ` as ${entry.invoice_number}`
                          : ''}{' '}
                        ·{' '}
                        {String(entry.provider_state || 'unknown').replaceAll(
                          '_',
                          ' '
                        )}
                      </span>
                      {entry.invoice_id && (
                        <span className="inline-flex items-center gap-2">
                          <a
                            href={`/admin/invoices?search=${encodeURIComponent(
                              entry.invoice_number || entry.invoice_id
                            )}`}
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            <EyeIcon className="h-3.5 w-3.5" />
                            View invoice
                          </a>
                          {entry.provider_state === 'recoverable' && (
                            <button
                              type="button"
                              onClick={() =>
                                handleRetryInvoiceSync(entry.invoice_id)
                              }
                              disabled={retryingInvoiceId === entry.invoice_id}
                              className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50"
                            >
                              <ArrowPathIcon
                                className={`h-3.5 w-3.5 ${
                                  retryingInvoiceId === entry.invoice_id
                                    ? 'animate-spin'
                                    : ''
                                }`}
                              />
                              Retry Stripe sync
                            </button>
                          )}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          {Array.isArray(batch.processing_result.skipped_rows) &&
            batch.processing_result.skipped_rows.length > 0 && (
              <div className="mt-3 rounded-card bg-surface-container-lowest p-3 text-xs text-on-surface-variant">
                <p className="font-medium text-on-surface">
                  Excluded or invalid rows
                </p>
                <ul className="mt-2 space-y-1">
                  {batch.processing_result.skipped_rows
                    .slice(0, 5)
                    .map((entry: any, index: number) => (
                      <li key={`${entry.row_number || index}`}>
                        Row {entry.row_number || 'unknown'}
                        {entry.reservation_ref
                          ? ` for reservation ${entry.reservation_ref}`
                          : ''}
                        :{' '}
                        {(entry.reasons || []).join('; ') ||
                          entry.validation_status ||
                          'Not eligible for invoice creation'}
                        .
                      </li>
                    ))}
                </ul>
                {batch.processing_result.skipped_rows.length > 5 && (
                  <p className="mt-2">
                    Showing 5 of {batch.processing_result.skipped_rows.length}{' '}
                    excluded or invalid rows.
                  </p>
                )}
              </div>
            )}
          {Array.isArray(batch.processing_result.duplicate_invoices_skipped) &&
            batch.processing_result.duplicate_invoices_skipped.length > 0 && (
              <div className="mt-3 rounded-card bg-surface-container-lowest p-3 text-xs text-on-surface-variant">
                <p className="font-medium text-on-surface">
                  Skipped existing invoices
                </p>
                <ul className="mt-2 space-y-1">
                  {batch.processing_result.duplicate_invoices_skipped
                    .slice(0, 5)
                    .map((entry: any, index: number) => (
                      <li key={`${entry.existing_invoice_id || index}`}>
                        Rows {(entry.row_numbers || []).join(', ') || 'unknown'}{' '}
                        matched{' '}
                        {entry.existing_invoice_number || 'an existing invoice'}
                        {entry.reservation_ref
                          ? ` for reservation ${entry.reservation_ref}`
                          : ''}
                        .
                      </li>
                    ))}
                </ul>
                {batch.processing_result.duplicate_invoices_skipped.length >
                  5 && (
                  <p className="mt-2">
                    Showing 5 of{' '}
                    {batch.processing_result.duplicate_invoices_skipped.length}{' '}
                    skipped invoice groups.
                  </p>
                )}
              </div>
            )}
        </div>
      )}

      {/* Pending changes banner */}
      {adjustmentCount > 0 && batch.status === 'pending_review' && (
        <div className="rounded-card border border-info-container/70 bg-info-container/25 p-3 mb-4 flex items-center justify-between">
          <span className="text-sm text-on-surface">
            <PencilIcon className="w-4 h-4 inline mr-1" />
            {adjustmentCount} pending change(s) — click Approve to save
          </span>
          <button onClick={() => setAdjustments(new Map())} className="text-sm font-medium text-primary hover:underline">
            Reset All
          </button>
        </div>
      )}

      {/* Deferred Customer Resolution Section */}
      {isReviewMode && deferredCustomerResolution.length > 0 && (
        <div className="rounded-card border border-warning-container/70 bg-warning-container/25 p-4 mb-6">
          <h3 className="text-sm font-medium text-on-surface mb-3">
            <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />
            Stripe customer linking at finalize ({deferredCustomerResolution.length} customer IDs, {unmatchedCount} items)
          </h3>
          <p className="text-xs text-on-surface-variant mb-3">
            Customers are not created during import. When you finalize an invoice, ModalOS checks the connected Stripe account for an existing customer with the same email and links that invoice automatically. If no Stripe customer exists for the email, one is created during finalization.
          </p>
          <div className="space-y-2">
            {deferredCustomerResolution.map((resolution) => {
              const hasExistingStripeMatch = resolution.resolution_type === 'link_existing';
              return (
                <div key={resolution.scsw_customer_id} className="bg-surface-container-lowest rounded-card px-4 py-3 border border-outline/60">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-sm font-medium text-on-surface">SCSW #{resolution.scsw_customer_id}</span>
                      <span className="text-xs text-on-surface-variant ml-2">({resolution.item_count} trip{resolution.item_count > 1 ? 's' : ''})</span>
                      <span className="text-xs text-on-surface-variant ml-2">— {resolution.customer_name}</span>
                      {resolution.customer_email && (
                        <span className="text-xs text-on-surface-variant ml-2">{resolution.customer_email}</span>
                      )}
                    </div>
                    <span className={`inline-flex items-center rounded-pill px-2.5 py-1 text-xs font-medium ${hasExistingStripeMatch ? 'bg-success-container text-success-on-container' : 'bg-warning-container text-warning-on-container'}`}>
                      {hasExistingStripeMatch ? 'Links to existing Stripe customer' : 'Creates Stripe customer on finalize'}
                    </span>
                  </div>
                  {hasExistingStripeMatch ? (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs font-medium text-success-on-container">
                        Matching Stripe customer record{resolution.matching_stripe_customers.length > 1 ? 's' : ''} by email:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {resolution.matching_stripe_customers.map((match) => (
                          <span key={match.id} className="inline-flex items-center rounded-pill bg-success-container border border-success-container/70 px-2.5 py-1 text-xs text-success-on-container">
                            {match.name}
                            <span className="ml-1 opacity-80">({match.id})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-warning-on-container">
                      No existing Stripe customer currently uses this email. Finalizing the invoice will create the Stripe customer from the imported profile data.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter + Sort + Bulk Actions */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex space-x-2">
            {(['all', 'valid', 'unmatched', 'skipped'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`rounded-pill px-3 py-1 text-sm font-medium ${filter === f ? 'bg-primary/15 text-primary' : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-highest'}`}
              >
                {f === 'all' && `All (${items.length})`}
                {f === 'valid' && `Ready (${validCount})`}
                {f === 'unmatched' && `Deferred (${unmatchedCount})`}
                {f === 'skipped' && `Already Billed (${skippedCount})`}
              </button>
            ))}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="input-field text-sm py-1 px-2"
          >
            <option value="row">Sort: Row #</option>
            <option value="customer">Sort: Customer ID</option>
            <option value="passenger">Sort: Passenger</option>
            <option value="date">Sort: Date</option>
            <option value="total">Sort: Total (high to low)</option>
            <option value="status">Sort: Match Status</option>
          </select>
        </div>
        {isReviewMode && selectedRows.size > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-on-surface-variant">{selectedRows.size} selected</span>
            <button
              onClick={() => {
                setAdjustments(prev => {
                  const next = new Map(prev);
                  for (const rowNum of Array.from(selectedRows)) {
                    const existing = next.get(rowNum);
                    next.set(rowNum, { row_number: rowNum, ...existing, exclude: true });
                  }
                  return next;
                });
                setSelectedRows(new Set());
              }}
              className="btn-secondary inline-flex items-center gap-2 text-error"
            >
              <TrashIcon className="w-4 h-4 mr-1" /> Exclude Selected
            </button>
            <button onClick={() => setSelectedRows(new Set())} className="text-sm text-on-surface-variant hover:text-on-surface">
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Items Table */}
      <div className="admin-table-card overflow-x-auto">
        <table className="min-w-full divide-y divide-outline text-sm">
          <thead className="bg-surface-container-low">
            <tr>
              {isReviewMode && <th className="px-3 py-3 text-center text-xs font-medium text-on-surface-variant uppercase w-10">
                <input type="checkbox"
                  checked={filteredItems.length > 0 && filteredItems.filter(i => !i.skipped).every(i => selectedRows.has(i.row_number))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRows(new Set(filteredItems.filter(i => !i.skipped).map(i => i.row_number)));
                    } else {
                      setSelectedRows(new Set());
                    }
                  }}
                  className="rounded border-ghost text-primary focus:ring-primary"
                />
              </th>}
              <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">SCSW#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Passenger</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Res#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Route</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Vehicle</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-on-surface-variant uppercase">Total</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Matched SCSW Customer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Invoice / Account Billing Contact</th>
              {isReviewMode && <th className="px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-outline">
            {filteredItems.map((item) => {
              const adj = adjustments.get(item.row_number);
              const isExcluded = adj?.exclude || item.skipped;
              const originalItem = items.find(i => i.row_number === item.row_number)!;
              const stripeResolution = deferredResolutionByRow.get(item.row_number);
              const selectedContactAction =
                item.contact_reconciliation_action || 'use_for_invoice_only';
              const availableContactActions = item.available_contact_actions?.length
                ? item.available_contact_actions
                : ['use_for_invoice_only' as ContactReconciliationAction];
              const contactResult = originalItem.contact_reconciliation_result;

              return (
                <tr key={item.row_number} className={isExcluded ? 'bg-surface-container-low text-on-surface-variant line-through' : item.validation_status === 'error' ? 'bg-error-container/20' : ''}>
                  {isReviewMode && (
                    <td className="px-3 py-2 text-center">
                      {!originalItem.skipped && (
                        <input
                          type="checkbox"
                          checked={selectedRows.has(item.row_number)}
                          onChange={() => toggleRowSelection(item.row_number)}
                          className="rounded border-ghost text-primary focus:ring-primary"
                        />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-2 text-on-surface-variant">{item.row_number}</td>
                  <td className="px-4 py-2">
                    {originalItem.skipped && originalItem.original_invoice_number ? (
                      <span className="text-on-surface-variant text-xs" title={`Already billed as ${originalItem.original_invoice_number}`}>Billed ({originalItem.original_invoice_number})</span>
                    ) : isExcluded ? (
                      <span className="text-on-surface-variant text-xs">Excluded</span>
                    ) : item.validation_status === 'valid' ? (
                      <CheckCircleIcon className="w-4 h-4 text-success" />
                    ) : item.matched_customer_id ? (
                      <CheckCircleIcon className="w-4 h-4 text-success" />
                    ) : (
                      <XCircleIcon className="w-4 h-4 text-error" />
                    )}
                  </td>
                  <td className="px-4 py-2">{item.scsw_customer_id}</td>
                  <td className="px-4 py-2">{item.passenger_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{item.reservation_ref}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{item.pickup_date} {item.pickup_time}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-xs">{item.pickup_location} → {item.dropoff_location}</td>
                  <td className="px-4 py-2">{item.vehicle_type}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatCents(item.total)}</td>
                  <td className="px-4 py-2">
                    {originalItem.matched_customer_name ? (
                      <div className="space-y-1 text-xs">
                        <span className="font-medium text-success">{originalItem.matched_customer_name}</span>
                        {(item.imported_account_name || item.matched_account_name) && (
                          <div className="text-on-surface-variant">
                            Account: {item.matched_account_name || 'No saved account'}
                            {item.imported_account_name && item.imported_account_name !== item.matched_account_name
                              ? ` · Imported ${item.imported_account_name}`
                              : ''}
                          </div>
                        )}
                        {item.account_name_mismatch && (
                          <span className="inline-flex rounded-pill bg-warning-container px-2 py-0.5 text-[11px] font-medium text-warning-on-container">
                            Account differs
                          </span>
                        )}
                      </div>
                    ) : stripeResolution?.preferred_stripe_customer ? (
                      <span className="text-success text-xs">
                        Stripe email match: {stripeResolution.preferred_stripe_customer.name}
                      </span>
                    ) : item.validation_status === 'valid' ? (
                      <span className="text-warning text-xs">Creates or links on finalize</span>
                    ) : !isExcluded ? (
                      <span className="text-error text-xs">No match</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 min-w-[260px]">
                    <div className="space-y-1 text-xs">
                      <div>
                        <span className="font-medium text-on-surface">Imported:</span>{' '}
                        <span className="text-on-surface-variant">{formatContact(item.imported_billing_contact)}</span>
                      </div>
                      <div>
                        <span className="font-medium text-on-surface">Saved:</span>{' '}
                        <span className="text-on-surface-variant">{formatContact(item.saved_contact)}</span>
                      </div>
                      {item.billing_contact_mismatch && (
                        <span className="inline-flex rounded-pill bg-warning-container px-2 py-0.5 text-[11px] font-medium text-warning-on-container">
                          Contact differs
                        </span>
                      )}
                      {contactResult && contactResult.status !== 'not_required' && (
                        <div className={contactResult.status === 'failed' || contactResult.status === 'skipped' ? 'text-warning' : 'text-success'}>
                          {getContactResultMessage(contactResult)}
                        </div>
                      )}
                    </div>
                  </td>
                  {isReviewMode && (
                    <td className="px-4 py-2 whitespace-nowrap">
                      {!originalItem.skipped && (
                        <div className="flex items-center space-x-2">
                          <select
                            value={selectedContactAction}
                            onChange={(event) => handleContactActionChange(item.row_number, event.target.value as ContactReconciliationAction)}
                            className="input-field text-xs py-1 px-2 min-w-[190px]"
                            title={contactActionDescriptions[selectedContactAction]}
                          >
                            {availableContactActions.map((action) => (
                              <option key={action} value={action}>
                                {contactActionLabels[action]}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleExcludeItem(item.row_number)}
                            className={isExcluded ? 'text-success hover:underline text-xs' : 'text-error hover:underline text-xs'}
                            title={isExcluded ? 'Include' : 'Exclude'}
                          >
                            {isExcluded ? <CheckCircleIcon className="w-4 h-4" /> : <XMarkIcon className="w-4 h-4" />}
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredItems.length === 0 && (
          <div className="p-8 text-center text-on-surface-variant">No items match this filter.</div>
        )}
      </div>

      {/* Validation Errors */}
      {batch.invalid_items > 0 && (
        <div className="mt-6 rounded-card border border-error-container/70 bg-error-container/20 p-4">
          <h3 className="text-sm font-medium text-on-surface mb-2">Validation Errors</h3>
          <ul className="space-y-1">
            {items.filter(i => i.validation_errors?.length > 0).slice(0, 20).map((item) => (
              <li key={item.row_number} className="text-sm text-error-on-container">
                Row {item.row_number}: {item.validation_errors.join('; ')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Stat Card
// ============================================================

function StatCard({ label, value, color, isText }: { label: string; value: number | string; color?: string; isText?: boolean }) {
  const colorMap: Record<string, string> = {
    green: 'text-success',
    red: 'text-error',
    blue: 'text-primary',
    yellow: 'text-warning',
    gray: 'text-on-surface-variant',
  };
  return (
    <div className="admin-subpanel rounded-card p-4">
      <div className="text-xs font-medium text-on-surface-variant uppercase">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${color ? colorMap[color] : 'text-on-surface'}`}>{value}</div>
    </div>
  );
}
